/**
 * BooksHub — Batch #14.271
 * Sidebar > Tenue de livres. Sélecteur de dossier (expertise `tenue_livres`) + tuiles :
 * Clients · Fournisseurs · Achats · Ventes · Encaissements · Décaissements · Journal général · Grand livre · Rapports · Plan comptable.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Calculator, Check, ChevronsUpDown, Loader2, Users } from "lucide-react";
import { toast } from "@/lib/toast";
import { api } from "@/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { money } from "../comptabilite/_shared";
import { BOOKS_TILES, TONE_BG, booksPath } from "./_books";
import EntreprisePortalControls from "./EntreprisePortalControls";

function Tile({ t, nb, badgeTitle, onClick }) {
  return (
    <button onClick={onClick}
      className={`relative group bg-gradient-to-br ${TONE_BG[t.tone]} border-2 rounded-2xl p-5 flex flex-col items-center justify-center text-center transition-all duration-200 transform hover:scale-105 active:scale-95 hover:shadow-lg`}
      data-testid={`books-hub-tile-${t.id}`}>
      {nb > 0 && <span className={`absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded-full text-white shadow ${t.ent ? "bg-rose-500" : "bg-orange-500"}`} title={badgeTitle} data-testid={`books-hub-badge-${t.id}`}>{nb}</span>}
      <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center mb-3 group-hover:shadow-md transition">
        <t.icon className="w-7 h-7" />
      </div>
      <span className="font-semibold text-sm mb-0.5">{t.title}</span>
      <span className="text-[10px] opacity-75">{t.desc}</span>
    </button>
  );
}

function Summary({ ov }) {
  if (!ov) return null;
  return (
    <div className="flex items-center gap-2 text-[11px] flex-wrap justify-end" data-testid="books-hub-summary">
      <span className="px-2 py-1 rounded-lg bg-white border border-slate-200 text-slate-600">Banque <strong className="font-mono" data-testid="books-hub-bank">{money(ov.bank)}</strong></span>
      <span className={`px-2 py-1 rounded-lg border ${ov.receivable > 0 ? "bg-sky-100 border-sky-200 text-sky-800" : "bg-white border-slate-200 text-slate-600"}`}>À recevoir <strong className="font-mono" data-testid="books-hub-receivable">{money(ov.receivable)}</strong></span>
      <span className={`px-2 py-1 rounded-lg border ${ov.payable > 0 ? "bg-rose-100 border-rose-200 text-rose-800" : "bg-white border-slate-200 text-slate-600"}`}>À payer <strong className="font-mono" data-testid="books-hub-payable">{money(ov.payable)}</strong></span>
      {ov.overdue_sales > 0 && <span className="px-2 py-1 rounded-lg bg-amber-100 border border-amber-200 text-amber-800 font-semibold">{ov.overdue_sales} vente(s) en retard</span>}
      {ov.drafts?.entries > 0 && <span className="px-2 py-1 rounded-lg bg-orange-100 border border-orange-200 text-orange-800 font-semibold" data-testid="books-hub-draft-entries">{ov.drafts.entries} écriture(s) à reporter au GL</span>}
      {!ov.nb_accounts && <span className="px-2 py-1 rounded-lg bg-orange-100 border border-orange-200 text-orange-800 font-semibold" data-testid="books-hub-no-plan">Plan comptable à installer</span>}
    </div>
  );
}

export default function BooksHub() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [masters, setMasters] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedId, setSelectedId] = useState(searchParams.get("master_id") || "");
  const [openCombo, setOpenCombo] = useState(false);
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/masters", { params: { expertise: "tenue_livres" } });
        setMasters(r.data?.items || []);
        if (!selectedId && (r.data?.items || []).length === 1) setSelectedId(r.data.items[0].id);
      } catch {
        toast.error("Erreur chargement des dossiers");
      } finally {
        setLoadingList(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchParams.get("qbo") === "error") toast.error(`Connexion QuickBooks refusée : ${searchParams.get("msg") || "erreur"}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedId) setSearchParams({ master_id: selectedId }, { replace: true });
  }, [selectedId, setSearchParams]);

  useEffect(() => {
    if (!selectedId) return;
    setOverview(null);
    api.get(`/books/${selectedId}/overview`).then((r) => setOverview(r.data)).catch(() => setOverview(null));
  }, [selectedId]);

  // Batch #14.313 — saisies client Entrepreneur non vues / à qualifier (pastilles sur les tuiles Entrepreneur)
  const [pending, setPending] = useState({});
  useEffect(() => {
    api.get("/admin/entrepreneurs/pending-summary").then((r) => setPending(r.data?.by_master || {})).catch(() => setPending({}));
  }, [selectedId]);

  const selectedMaster = useMemo(() => masters.find((m) => m.id === selectedId), [masters, selectedId]);

  const openTile = useCallback((tile) => {
    if (!selectedId) { toast.message("Sélectionnez un dossier client"); return; }
    navigate(booksPath(selectedId, tile.id));
  }, [navigate, selectedId]);

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-6xl mx-auto" data-testid="books-hub">
      <div className="bg-gradient-to-r from-teal-50 via-teal-50 to-white border-2 border-teal-200 rounded-2xl p-5 sm:p-6 shadow-sm">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-11 h-11 rounded-xl bg-teal-600 text-white flex items-center justify-center shadow">
            <Calculator className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-slate-900">Tenue de livres</h1>
            <p className="text-xs text-slate-600 mt-0.5">
              Clients, fournisseurs, ventes, achats, encaissements et décaissements d'un client — avec grand livre, plan comptable, états financiers, et le volet Entreprise (aperçu, réglages Entreprise & taxes, TPS/TVQ). Les factures, reçus et trajets envoyés par le client arrivent dans Files PMS › Entreprise.
            </p>
          </div>
          {selectedMaster && (
            <div className="flex flex-col items-end gap-2">
              {overview?.settings?.legal_name && <span className="text-xs font-semibold text-teal-900" data-testid="books-hub-legal-name">{overview.settings.legal_name}</span>}
              <Summary ov={overview} />
              <EntreprisePortalControls master={selectedMaster} />
            </div>
          )}
        </div>

        <div className="mt-5">
          <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider block mb-1.5">Dossier client</label>
          <Popover open={openCombo} onOpenChange={setOpenCombo}>
            <PopoverTrigger asChild>
              <button type="button" aria-expanded={openCombo} disabled={loadingList}
                className="w-full inline-flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm text-left transition shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                data-testid="books-hub-combobox-trigger">
                <span className="truncate flex-1">
                  {loadingList ? (
                    <span className="inline-flex items-center gap-2 text-slate-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement…</span>
                  ) : selectedMaster ? (
                    <span className="text-slate-800 font-medium inline-flex items-center gap-2">{selectedMaster.couple_display_name || "(sans nom)"}
                      {pending[selectedMaster.id] > 0 && <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold" title="Saisies client (volet Entreprise) à vérifier" data-testid="books-hub-pending-selected">{pending[selectedMaster.id]}</span>}
                    </span>
                  ) : (
                    <span className="text-slate-400">Choisir un dossier client…</span>
                  )}
                </span>
                <ChevronsUpDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[280px]" align="start">
              <Command>
                <CommandInput placeholder="Rechercher un dossier…" />
                <CommandList>
                  <CommandEmpty>
                    {masters.length === 0
                      ? "Aucun dossier avec l'expertise Tenue de livres activée — activez-la dans la fiche du dossier client (Cabinet › Master)."
                      : "Aucun résultat."}
                  </CommandEmpty>
                  <CommandGroup heading={`${masters.length} dossier(s)`}>
                    {masters.map((m) => (
                      <CommandItem key={m.id}
                        value={`${m.couple_display_name || ""} ${m.primary_name || ""} ${m.secondary_name || ""} ${m.primary_email || ""} ${m.client_number || ""}`}
                        onSelect={() => { setSelectedId(m.id); setOpenCombo(false); }}
                        className="flex items-start gap-2 py-2 cursor-pointer" data-testid={`books-hub-master-${m.id}`}>
                        <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${selectedId === m.id ? "opacity-100 text-teal-600" : "opacity-0"}`} />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-800 truncate">{m.client_number && <span className="font-mono text-teal-700 mr-1.5">{m.client_number}</span>}{m.couple_display_name || "(sans nom)"}</div>
                          {(m.primary_name || m.secondary_name) && (
                            <div className="text-[11px] text-slate-500 inline-flex items-center gap-1 mt-0.5">
                              <Users className="w-3 h-3" />{m.primary_name || "?"}{m.secondary_name ? ` + ${m.secondary_name}` : ""}
                            </div>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {!selectedId ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-16 text-center">
          <Calculator className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-slate-700">Sélectionnez un dossier client</h3>
          <p className="text-xs text-slate-500 mt-1">Les modules de tenue de livres apparaîtront ci-dessous.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="books-hub-tiles-books">
          {BOOKS_TILES.filter((t) => !t.cabinetOnly).map((t) => {
            const nb = t.ent
              ? (t.id === "apercu" ? pending[selectedId] || 0 : 0)
              : (t.drafts || []).reduce((s, k) => s + (overview?.drafts?.[k] || 0), 0);
            return <Tile key={t.id} t={t} nb={nb} badgeTitle={t.ent ? "Pièces reçues du portail à traiter (Files PMS › Entreprise)" : "Pièces non reportées"} onClick={() => openTile(t)} />;
          })}
        </div>
      )}
    </div>
  );
}
